import { useNavigate } from "react-router-dom";
import { formatNOK } from "@/lib/format";
import type { DBEquipmentItem, DBEquipmentUsage } from "@/lib/db-types";
import type { BookingState } from "@/lib/equipment/availability";
import { BookingStateBadge } from "./BookingStateBadge";

interface EquipmentTableProps {
  items: DBEquipmentItem[];
  categoryName: (id: string) => string;
  departmentName: (id: string | null) => string;
  /** Forsikringsverdi vises bare for admin og utstyrsansvarlige. */
  showAmounts: boolean;
  /** VIB-2919: status avledet fra bookingene (Ledig, Booket, ⚠, På service). */
  stateFor: (item: DBEquipmentItem) => BookingState;
  /** VIB-2919: «Brukt 12 mnd». */
  usageFor: (id: string) => DBEquipmentUsage | undefined;
}

const th = "py-2 px-3 font-medium text-muted-foreground text-xs uppercase tracking-wider whitespace-nowrap";

/** Én tabell for hele registeret med Kategori-kolonne (skjerm A). */
export function EquipmentTable({ items, categoryName, departmentName, showAmounts, stateFor, usageFor }: EquipmentTableProps) {
  const navigate = useNavigate();
  if (items.length === 0) {
    return <p className="text-sm text-muted-foreground p-4">Ingen utstyr matcher filteret.</p>;
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs sm:text-sm">
        <thead>
          <tr className="border-b">
            <th className={`${th} text-left`}>Navn</th>
            <th className={`${th} text-left`}>Kategori</th>
            <th className={`${th} text-left`}>Sted</th>
            <th className={`${th} text-right`}>Antall</th>
            <th className={`${th} text-left`}>Serienummer</th>
            {showAmounts && <th className={`${th} text-right`}>Forsikringsverdi</th>}
            <th className={`${th} text-right`}>Brukt 12 mnd</th>
            <th className={`${th} text-right`}>Leie/døgn</th>
            <th className={`${th} text-left`}>Status</th>
          </tr>
        </thead>
        <tbody>
          {items.map((i) => (
            <tr
              key={i.id}
              onClick={() => navigate(`/utstyr/${i.id}`)}
              className="border-b border-border/50 last:border-0 hover:bg-muted/30 cursor-pointer"
            >
              <td className="py-2.5 px-3">
                <a
                  href={`/utstyr/${i.id}`}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    navigate(`/utstyr/${i.id}`);
                  }}
                  className="font-medium hover:underline"
                >
                  {i.name}
                </a>
                {i.comment && <div className="text-xs text-muted-foreground line-clamp-1">{i.comment}</div>}
              </td>
              <td className="py-2.5 px-3 text-muted-foreground whitespace-nowrap">{categoryName(i.category_id)}</td>
              <td className="py-2.5 px-3 text-muted-foreground">{departmentName(i.department_id)}</td>
              <td className="py-2.5 px-3 text-right tabular-nums">{i.quantity}</td>
              <td className="py-2.5 px-3 font-mono text-xs">{i.serial_numbers.join(", ")}</td>
              {showAmounts && (
                <td className="py-2.5 px-3 text-right tabular-nums whitespace-nowrap">
                  {i.insurance_value != null ? formatNOK(i.insurance_value) : "–"}
                </td>
              )}
              <td className="py-2.5 px-3 text-right tabular-nums whitespace-nowrap">{usageFor(i.id)?.booked_days ?? 0} d</td>
              <td className="py-2.5 px-3 text-right tabular-nums whitespace-nowrap">
                {i.rental_price_day != null ? formatNOK(i.rental_price_day) : "–"}
              </td>
              <td className="py-2.5 px-3">
                <BookingStateBadge state={stateFor(i)} status={i.status} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
